/**
 * DialogBox — the classic Pokémon MESSAGE BOX: a cream/white rounded box with a crisp
 * dark double-border, dark readable text, and a small BLINKING ▼ continue arrow in the
 * bottom-right (the iconic "press to continue" indicator). Typewriter reveal (calm,
 * readable), TAP-TO-ADVANCE: first tap completes the current line, the next advances
 * to the next page. Works by touch, mouse, AND keyboard (Space/Enter). Respects reduced
 * motion (instant reveal + no blink). Pure reveal math lives in ./typewriter.ts.
 *
 * A tasteful GB/GBC nod — clean font, no janky pixel restyle. Public API is unchanged
 * (constructor options, show(), tap(), isPageComplete, onDone).
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TIMING } from './theme.js';
import { makeClickable } from './clickable.js';
import {
  advance,
  complete,
  createTypewriter,
  isComplete,
  visibleText,
  type TypewriterState,
} from './typewriter.js';

export interface DialogBoxOptions {
  readonly width: number;
  readonly height: number;
  readonly fontSize?: number;
  readonly reducedMotion?: boolean;
  /** Called after the LAST page is tapped past (the box may then be hidden). */
  readonly onDone?: () => void;
}

export class DialogBox extends Phaser.GameObjects.Container {
  private readonly box: Phaser.GameObjects.Graphics;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly speakerText: Phaser.GameObjects.Text;
  private readonly advanceHint: Phaser.GameObjects.Text;
  private readonly bWidth: number;
  private readonly bHeight: number;
  private readonly msPerChar: number;
  private readonly reducedMotion: boolean;
  private readonly onDone?: () => void;

  private pages: string[] = [];
  private pageIndex = 0;
  private tw: TypewriterState = createTypewriter('', 0);
  private arrowBlink?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene, x: number, y: number, options: DialogBoxOptions) {
    super(scene, x, y);
    this.bWidth = options.width;
    this.bHeight = options.height;
    this.reducedMotion = options.reducedMotion ?? false;
    this.msPerChar = this.reducedMotion ? 0 : TIMING.typewriter;
    this.onDone = options.onDone;
    const fontSize = options.fontSize ?? FONT.size.md;

    // The classic cream message box with a dark double border.
    this.box = scene.add.graphics();
    this.drawBox();

    this.speakerText = scene.add.text(
      -this.bWidth / 2 + SPACING.md,
      -this.bHeight / 2 + SPACING.sm,
      '',
      {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxText,
        fontStyle: 'bold',
      },
    );

    this.bodyText = scene.add.text(
      -this.bWidth / 2 + SPACING.md,
      -this.bHeight / 2 + SPACING.lg + SPACING.sm,
      '',
      {
        fontFamily: FONT.family,
        fontSize: `${fontSize}px`,
        color: CSS.boxText,
        wordWrap: { width: this.bWidth - SPACING.md * 2 },
        lineSpacing: 4,
      },
    );

    this.advanceHint = scene.add
      .text(this.bWidth / 2 - SPACING.md, this.bHeight / 2 - SPACING.sm, '▼', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.md}px`,
        color: CSS.boxText,
      })
      .setOrigin(1, 1)
      .setVisible(false);

    this.add([this.box, this.speakerText, this.bodyText, this.advanceHint]);

    this.setSize(this.bWidth, this.bHeight);
    // Centered hit rect matching the drawn box (origin 0.5) + a pointer cursor, since
    // the whole box is a tap-to-advance target. The explicit centered rect (not the
    // default top-left one) is what keeps the area aligned with the visual.
    makeClickable(this, this.bWidth, this.bHeight, { onClick: () => this.tap() });

    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());

    // Gentle blink of the ▼ continue arrow (steady when reduced-motion).
    if (!this.reducedMotion) {
      this.arrowBlink = scene.time.addEvent({
        delay: 480,
        loop: true,
        callback: () => {
          if (this.visible && isComplete(this.tw)) {
            this.advanceHint.setVisible(!this.advanceHint.visible);
          }
        },
      });
    }

    scene.add.existing(this);
  }

  /**
   * Show a message (split into `pages` by the caller, or one page). `speaker` is an
   * optional name shown above the body. Resets to the first page.
   */
  show(pages: string | readonly string[], speaker = ''): this {
    this.pages = (Array.isArray(pages) ? pages : [pages]) as string[];
    if (this.pages.length === 0) this.pages = [''];
    this.pageIndex = 0;
    this.speakerText.setText(speaker);
    this.setVisible(true);
    this.startPage();
    return this;
  }

  /** True once the current page is fully revealed (caller may gate input on this). */
  get isPageComplete(): boolean {
    return isComplete(this.tw);
  }

  /**
   * The TAP-TO-ADVANCE gesture (also the keyboard entry point). If the line is still
   * typing, reveal it all; otherwise move to the next page (or finish).
   */
  tap(): void {
    if (!isComplete(this.tw)) {
      complete(this.tw);
      this.render();
      return;
    }
    if (this.pageIndex < this.pages.length - 1) {
      this.pageIndex += 1;
      this.startPage();
    } else {
      this.setVisible(false);
      this.onDone?.();
    }
  }

  override destroy(fromScene?: boolean): void {
    this.scene?.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate, this);
    this.arrowBlink?.remove();
    this.arrowBlink = undefined;
    super.destroy(fromScene);
  }

  private drawBox(): void {
    const g = this.box;
    const w = this.bWidth;
    const h = this.bHeight;
    // The classic G/S message box is near-square; a small radius keeps it clean.
    const r = RADIUS.sm;
    g.clear();
    // Soft drop shadow for a hint of depth.
    g.fillStyle(COLORS.scrim, 0.28);
    g.fillRoundedRect(-w / 2 + 2, -h / 2 + 6, w, h, r);
    // Cream paper fill.
    g.fillStyle(COLORS.boxFill, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, r);
    // The iconic G/S dark double border: thick outer frame + soft inner line.
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, r);
    g.lineStyle(1, COLORS.boxBorderInner, 1);
    g.strokeRoundedRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, Math.max(2, r - 2));
  }

  private startPage(): void {
    this.tw = createTypewriter(this.pages[this.pageIndex] ?? '', this.msPerChar);
    this.render();
  }

  private onUpdate(_time: number, delta: number): void {
    if (!this.visible) return;
    if (isComplete(this.tw)) return;
    advance(this.tw, delta);
    this.render();
  }

  private render(): void {
    this.bodyText.setText(visibleText(this.tw));
    this.advanceHint.setVisible(isComplete(this.tw));
  }
}
